"use client";

import { cn } from "@/lib/utils";
import { Check, X, ShieldAlert, Loader2 } from "lucide-react";
import * as React from "react";
import { createContext, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";

// ---------------------------------------------------------------------------
// Context – shares approval & state across compound components
// ---------------------------------------------------------------------------

type ConfirmationState =
  | "input-streaming"
  | "input-available"
  | "approval-requested"
  | "approval-responded"
  | "output-denied"
  | "output-available";

interface ConfirmationContextValue {
  approval?: { id: string; approved?: boolean } | undefined;
  state: ConfirmationState;
}

const ConfirmationContext = createContext<ConfirmationContextValue | null>(null);

function useConfirmation() {
  const ctx = useContext(ConfirmationContext);
  if (!ctx)
    throw new Error(
      "Confirmation compound components must be used inside <Confirmation>"
    );
  return ctx;
}

// ---------------------------------------------------------------------------
// <Confirmation />
// ---------------------------------------------------------------------------

export type ConfirmationProps = {
  approval?: { id: string; approved?: boolean } | undefined;
  state: ConfirmationState | string;
  className?: string;
  children: React.ReactNode;
};

export function Confirmation({
  approval,
  state,
  children,
  className,
}: ConfirmationProps) {
  // Don't render for input-streaming / input-available states
  if (state === "input-streaming" || state === "input-available") return null;

  const isRequested = state === "approval-requested";
  const isAccepted =
    approval?.approved === true &&
    (state === "approval-responded" ||
      state === "output-denied" ||
      state === "output-available");
  const isRejected =
    approval?.approved === false &&
    (state === "approval-responded" ||
      state === "output-denied" ||
      state === "output-available");

  return (
    <ConfirmationContext.Provider
      value={{ approval, state: state as ConfirmationState }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={isRequested ? "request" : isAccepted ? "accepted" : "rejected"}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className={cn(
            "relative overflow-hidden rounded-xl border text-sm",
            // Border color per state
            isRequested &&
              "border-amber-300/60 bg-amber-50/60 dark:border-amber-500/20 dark:bg-amber-950/20",
            isAccepted &&
              "border-emerald-300/60 bg-emerald-50/60 dark:border-emerald-500/20 dark:bg-emerald-950/20",
            isRejected &&
              "border-red-300/60 bg-red-50/60 dark:border-red-500/20 dark:bg-red-950/20",
            // Fallback when no specific state matched
            !isRequested &&
              !isAccepted &&
              !isRejected &&
              "border-[color:var(--card-border)] bg-[color:var(--card-bg)]",
            className
          )}
        >
          {/* Accent top bar */}
          <div
            className={cn(
              "absolute inset-x-0 top-0 h-0.5",
              isRequested && "bg-amber-400 dark:bg-amber-500",
              isAccepted && "bg-emerald-400 dark:bg-emerald-500",
              isRejected && "bg-red-400 dark:bg-red-500",
              !isRequested &&
                !isAccepted &&
                !isRejected &&
                "bg-[color:var(--nt-primary)]"
            )}
          />

          <div className="px-4 py-3.5">{children}</div>
        </motion.div>
      </AnimatePresence>
    </ConfirmationContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// <ConfirmationRequest />
// ---------------------------------------------------------------------------

export function ConfirmationRequest({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { state } = useConfirmation();
  if (state !== "approval-requested") return null;

  return (
    <div className={cn("flex items-start gap-3", className)}>
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/40">
        <ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-1">
          Aprovação Necessária
        </p>
        <div className="text-sm text-[color:var(--foreground)]">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// <ConfirmationAccepted />
// ---------------------------------------------------------------------------

export function ConfirmationAccepted({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  const { approval, state } = useConfirmation();
  const shouldRender =
    approval?.approved === true &&
    (state === "approval-responded" ||
      state === "output-denied" ||
      state === "output-available");

  if (!shouldRender) return null;

  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
        <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
      </div>
      <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
        {children || "Aprovado"}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// <ConfirmationRejected />
// ---------------------------------------------------------------------------

export function ConfirmationRejected({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  const { approval, state } = useConfirmation();
  const shouldRender =
    approval?.approved === false &&
    (state === "approval-responded" ||
      state === "output-denied" ||
      state === "output-available");

  if (!shouldRender) return null;

  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40">
        <X className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
      </div>
      <span className="text-sm font-medium text-red-700 dark:text-red-400">
        {children || "Rejeitado"}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// <ConfirmationActions />
// ---------------------------------------------------------------------------

export function ConfirmationActions({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { state } = useConfirmation();
  if (state !== "approval-requested") return null;

  return (
    <div
      className={cn(
        "flex items-center justify-end gap-2 mt-3 pt-3 border-t border-amber-200/60 dark:border-amber-500/10",
        className
      )}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// <ConfirmationAction />
// ---------------------------------------------------------------------------

type ConfirmationActionVariant =
  | "default"
  | "outline"
  | "destructive"
  | "approve"
  | "reject";

interface ConfirmationActionProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ConfirmationActionVariant;
  loading?: boolean;
}

const variantStyles: Record<ConfirmationActionVariant, string> = {
  default:
    "bg-[color:var(--nt-primary)] text-white hover:brightness-95 shadow-sm",
  outline:
    "border border-[color:var(--card-border)] bg-transparent text-[color:var(--foreground)] hover:bg-black/5 dark:hover:bg-white/5",
  destructive: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
  approve:
    "bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-700 shadow-sm",
  reject:
    "border border-red-200 dark:border-red-800/40 bg-transparent text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30",
};

export function ConfirmationAction({
  className,
  variant = "default",
  loading = false,
  disabled,
  children,
  ...props
}: ConfirmationActionProps) {
  return (
    <button
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-lg text-sm font-medium",
        "h-8 px-3.5 py-1.5",
        "transition-all duration-150 ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--nt-primary)] focus-visible:ring-offset-1",
        "disabled:pointer-events-none disabled:opacity-50",
        "cursor-pointer",
        variantStyles[variant],
        className
      )}
      {...props}
    >
      {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {children}
    </button>
  );
}
